const FLYING_ATTR = 'data-hero-logo-flying';
const GHOST_SELECTOR = '[data-hero-logo-reveal-element="ghost"]';
const FLYER_SELECTOR = '[data-hero-logo-reveal-element="flyer"]';
const TARGET_SELECTOR = '.header-section__wrapper .navbar__logo .logo';
const NAVBAR_SELECTOR = '.header-section__wrapper x-navbar';
const STICKY_WRAPPER_CLASS = 'header-section__wrapper--sticky';
const STATIC_CLASS = 'hero-logo-reveal__flyer--static';
const FILTER_FLIP_START = 0.7;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const smooth = (p) => p * p * (3 - 2 * p);

customElements.define('x-hero-logo-reveal', class extends HTMLElement {
    connectedCallback() {
        this._ghost = this.querySelector(GHOST_SELECTOR);
        this._flyer = this.querySelector(FLYER_SELECTOR);
        this._navbar = document.querySelector(NAVBAR_SELECTOR);
        this._target = document.querySelector(`${TARGET_SELECTOR} .logo__img img`)
            || document.querySelector(TARGET_SELECTOR);

        const stickyHeader = !!this._navbar?.closest(`.${STICKY_WRAPPER_CLASS}`);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!this._ghost || !this._flyer || !this._target || !stickyHeader || reduceMotion) {
            this._renderStatic();
            return;
        }

        const section = this.closest('.header-overlap-section');
        const overlapActive = !!section
            && section.parentElement?.id === 'MainPageContent'
            && section === section.parentElement.firstElementChild
            && this._navbar.classList.contains('navbar--overlap');
        this._inverted = overlapActive && this._navbar.classList.contains('navbar--inverted-logo');

        // Fixed positioning must not be affected by transformed/clipped ancestors
        document.body.appendChild(this._flyer);

        this._docked = false;
        this._started = false;
        this._baseWidth = 0;
        document.documentElement.setAttribute(FLYING_ATTR, '');
        this._tick = this._tick.bind(this);
        this._raf = requestAnimationFrame(this._tick);
    }

    disconnectedCallback() {
        cancelAnimationFrame(this._raf);
        document.documentElement.removeAttribute(FLYING_ATTR);
        if (this._flyer?.parentElement === document.body) {
            this._flyer.remove();
        }
    }

    _tick() {
        this._raf = requestAnimationFrame(this._tick);

        const scroll = window.scrollY;
        const ghost = this._ghost.getBoundingClientRect();
        const target = this._target.getBoundingClientRect();
        if (!ghost.width || !target.width) return;

        const ghostX = ghost.left + ghost.width / 2;
        const ghostY = ghost.top + ghost.height / 2;
        const targetX = target.left + target.width / 2;
        const targetY = target.top + target.height / 2;

        // Scroll distance until the two logo centers meet
        const travel = ghostY + scroll - targetY;
        const progress = travel > 0 ? clamp(scroll / travel, 0, 1) : 1;

        if (progress >= 1) {
            this._setDocked(true);
            return;
        }
        this._setDocked(false);

        const eased = smooth(progress);

        if (this._baseWidth !== ghost.width) {
            this._baseWidth = ghost.width;
            this._flyer.style.width = `${ghost.width}px`;
        }

        const x = ghostX + (targetX - ghostX) * eased;
        const y = ghostY + (targetY - ghostY) * eased;
        const scale = (ghost.width + (target.width - ghost.width) * eased) / ghost.width;
        this._flyer.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${scale})`;

        if (this._inverted) {
            // Same filter pair the navbar animates between, so the handoff is seamless
            const flip = clamp((progress - FILTER_FLIP_START) / (1 - FILTER_FLIP_START), 0, 1);
            this._flyer.style.filter = `brightness(${flip}) invert(${1 - flip})`;
        }

        if (!this._started) {
            this._started = true;
            this._flyer.hidden = false;
        }
    }

    _setDocked(docked) {
        if (this._docked === docked) return;
        this._docked = docked;
        this._flyer.style.visibility = docked ? 'hidden' : '';
        document.documentElement.toggleAttribute(FLYING_ATTR, !docked);
        if (!this._started && !docked) {
            this._started = true;
            this._flyer.hidden = false;
        }
    }

    // No sticky header or reduced motion: show the logo statically in the hero
    _renderStatic() {
        document.documentElement.removeAttribute(FLYING_ATTR);
        if (this._ghost) this._ghost.hidden = true;
        if (this._flyer) {
            this._flyer.classList.add(STATIC_CLASS);
            this._flyer.hidden = false;
        }
    }
});
